import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ShopBanner, ShopSection, ShopSettings } from '@infrab4a/connect';
import { MessageService } from 'primeng/api';
import { ImageUploadComponent } from 'src/app/components/image-upload/image-upload.component';
import { ShopSettingsService } from 'src/app/connect-api/api/shop-settings/shop-settings.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  MENSMARKET_HOME_BANNERS_PATH,
  MENSMARKET_HOME_DOCUMENT_ID,
  MENSMARKET_HOME_ROUTE
} from '../mens-new-home.constants';

export type DisplayRules = {
  subscriberBuyer: boolean;
  notSubscriber: boolean;
  subscriberNotBuyer: boolean;
  topBadgeSubscriber: boolean;
  beautyProfile: boolean;
};

export type Banner = {
  type: 'BANNER';
  fixed: boolean;
  editable: boolean;
  mobileImage: string;
  desktopImage: string;
  link: string;
  altText: string;
  countdown?: string;
  displayRules?: Array<Partial<DisplayRules>>;
  publishDate?: Date;
  expirationDate?: Date;
};

@Component({
  selector: 'app-mens-new-home-banner-form',
  templateUrl: './banner-form.component.html',
  styleUrl: './banner-form.component.scss'
})
export class MensNewHomeBannerFormComponent
  extends ImageUploadComponent<any>
  implements OnInit
{
  @Input() id: string | null = null;
  override form: FormGroup = new FormGroup({
    id: new FormControl<string>(''),
    type: new FormControl<string>('BANNER'),
    fixed: new FormControl<boolean>(false),
    editable: new FormControl<boolean>(true),
    mobileImage: new FormControl<string>('', [Validators.required]),
    desktopImage: new FormControl<string>('', [Validators.required]),
    link: new FormControl<string>('', [Validators.required]),
    altText: new FormControl<string>('', [Validators.required]),
    countdown: new FormControl<string>(''),
    displayRules: new FormGroup({
      subscriberBuyer: new FormControl(null),
      notSubscriber: new FormControl(null),
      subscriberNotBuyer: new FormControl(null),
      topBadgeSubscriber: new FormControl(null),
      beautyProfile: new FormControl(null)
    }),
    publishDate: new FormControl<Date | null>(null),
    expirationDate: new FormControl<Date | null>(null)
  });
  settings: Partial<ShopSettings> & { sections?: ShopSection[] };
  private messageService = inject(MessageService);
  private shopSettingsService = inject(ShopSettingsService);
  private activatedRoute = inject(ActivatedRoute);

  displayRuleOptions = [
    { label: 'Não se aplica', value: null },
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
  ];

  async ngOnInit(): Promise<void> {
    this.settings = await this.shopSettingsService.getById(MENSMARKET_HOME_DOCUMENT_ID);
    this.id = this.activatedRoute.snapshot.paramMap.get('id');

    if (this.id) {
      const section = this.settings.sections?.find((s) => s.id === this.id);
      if (section) {
        this.form.patchValue(section);
        if (
          (section as Banner).displayRules &&
          Array.isArray((section as Banner).displayRules)
        ) {
          for (const rule of (section as Banner).displayRules) {
            for (const key in rule) {
              if (this.form.get('displayRules')?.get(key)) {
                this.form.get('displayRules')?.get(key)?.setValue(rule[key]);
              }
            }
          }
        }
      }
    }
  }

  async onSubmit(): Promise<void> {
    if (this.form.valid) {
      LoaderService.showLoader();
      const newSection = this.getBannerInfo();
      const sections = this.settings.sections || [];
      const existingIndex = sections.findIndex((s) => s.id === this.id);

      if (existingIndex >= 0) {
        sections[existingIndex] = newSection;
      } else {
        sections.splice(sections.length - 1, 0, newSection);
      }

      await this.shopSettingsService.update({
        id: this.settings.id,
        sections: sections
      });

      await this.afterSubmit();
    }

    LoaderService.showLoader(false);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Banner salvo com sucesso'
    });

    this.router.navigate([MENSMARKET_HOME_ROUTE]);
  }

  private getBannerInfo(): any {
    const formValue = this.form.value;
    const displayRules = [];
    const rules = formValue.displayRules;
    for (const key in rules) {
      if (rules[key] !== null) {
        displayRules.push({ [key]: rules[key] });
      }
    }

    return {
      ...formValue,
      id: formValue.id || crypto.randomUUID(),
      displayRules: displayRules.length > 0 ? displayRules : []
    };
  }

  get mobileImageWidth(): number {
    return 690;
  }

  get mobileImageHeight(): number {
    return 350;
  }

  get desktopImageWidth(): number {
    return 2432;
  }

  get desktopImageHeight(): number {
    return 500;
  }

  override get filePath(): string {
    return MENSMARKET_HOME_BANNERS_PATH;
  }

  override get model(): ShopBanner {
    return null;
  }

  override get modelId(): string | number {
    return crypto.randomUUID();
  }
}
